import { useQuery } from "@tanstack/react-query";
import { ApiResponse } from "@/shared/types/api.ts";
import { useOrganizationStore } from "@/shared/stores/useOrganizationStore";
import { useSession } from "@/auth/contexts/auth-context.tsx";

import { QUERY_KEYS } from "@/locations/api/config.ts";
import {
  getAllLocations,
  getLocationById,
  IGetLocationByIdResponse,
  IGetLocationsResponse,
} from "@/locations/api/requests/location.requests.ts";

export const useGetAllLocations = (options?: {
  /** Poll interval in ms — e.g. the map repolls so machine `last_gps` stays current. */
  refetchInterval?: number | false;
}) => {
  const { selectedOrganizationId } = useOrganizationStore();
  const { accessToken, isLoading: authLoading } = useSession();

  return useQuery<ApiResponse<IGetLocationsResponse[]>>({
    queryKey: [QUERY_KEYS.LOCATION, selectedOrganizationId],
    queryFn: () => getAllLocations(),
    enabled: !!selectedOrganizationId && !!accessToken && !authLoading,
    staleTime:  1000 * 60 * 60 * 12, // 5 minutes - data considered fresh
    gcTime: 1000 * 60 * 60 * 24, // 24 hours - keep in cache
    refetchInterval: options?.refetchInterval ?? false,
  });
};

export const useGetLocationById = (id: number) => {
  const { selectedOrganizationId } = useOrganizationStore();
  const { accessToken, isLoading: authLoading } = useSession();

  return useQuery<ApiResponse<IGetLocationByIdResponse>>({
    queryKey: [QUERY_KEYS.LOCATION, id, selectedOrganizationId],
    queryFn: () => getLocationById(id),
    enabled: !!id && !!selectedOrganizationId && !!accessToken && !authLoading,
    staleTime:  1000 * 60 * 60 * 12, // 5 minutes - data considered fresh
    gcTime: 1000 * 60 * 60 * 24, // 24 hours - keep in cache
  });
};
